import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import BusinessCard from "./common/BusinessCard";
import { createGSAPContext } from "../hooks/useGSAPCleanup";
import { vw, vh, responsiveAnimation, responsiveScrollTrigger } from "../utils/responsiveAnimations";
import "./Section1Point.css";
import Spacer from "./common/Spacer";

gsap.registerPlugin(ScrollTrigger);

function Section1Point() {
  const sectionRef = useRef(null);
  const cardRef = useRef(null);
  const highlightRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const card = cardRef.current;
    const highlight = highlightRef.current;
    const logoOverlay = section?.querySelector(".logo-overlay");

    if (!section) return;

    const ctx = createGSAPContext(() => {
      // Remove all intro animations - messages visible from start

      // Enhanced card animation with responsive values
      const tl = gsap.timeline({
        scrollTrigger: responsiveScrollTrigger({
          trigger: section,
          start: "top bottom", // 섹션이 화면에 들어오자마자 시작
          end: "+=250%",
          scrub: 0.5,
        }),
      });

      tl.fromTo(
        card,
        responsiveAnimation({
          rotateY: 1080,
          rotateX: -60,
          rotateZ: 360,
          opacity: 0,
          scale: 0.1,
          y: -400,
          x: -300,
        }),
        {
          ...responsiveAnimation({
            rotateY: -30,
            rotateX: 15,
            rotateZ: -20,
            opacity: 1,
            scale: 1.2,
            y: 0,
            x: 0,
          }),
          duration: 3,
          ease: "power3.out",
        }
      )
        .to(
          card,
          {
            rotateY: 10,
            rotateX: -5,
            rotateZ: 8,
            scale: 1.05,
            duration: 0.8,
            ease: "sine.inOut",
          },
          "-=0.3"
        )
        .to(
          card,
          {
            rotateY: 0,
            rotateX: 0,
            rotateZ: 0,
            scale: 1,
            duration: 1,
            ease: "elastic.out(0.8, 0.4)",
          },
          "-=0.2"
        )
        .to(
          highlight,
          {
            opacity: 1,
            scale: 1.2,
            duration: 0.8,
            ease: "power2.out",
          },
          "-=0.5"
        );

      // Point title visible from start - no animation

      // Reveal section visible from start - no animation

      // Demo section - only animate the card, not the text
      const demoTl = gsap.timeline({
        scrollTrigger: {
          trigger: ".demo-section",
          start: "top 75%",
          toggleActions: "play none none reverse",
        },
      });

      // Only animate the card base
      gsap.set(".ali-card-base", { opacity: 0 });

      demoTl.fromTo(
        ".ali-card-base",
        {
          opacity: 0,
          scale: 0.95,
        },
        {
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "power2.out",
        }
      );

      // No text animation needed anymore

      // Logo overlay fade animation - scroll-based
      if (logoOverlay) {
        gsap.to(logoOverlay, {
          opacity: 1,
          scrollTrigger: {
            trigger: ".demo-section",
            start: "top 60%",
            end: "top 20%",
            scrub: 1, // 스크롤과 직접 연동
          },
        });
      }

      // All text messages are visible from the start - no animations
    }, section);

    return () => {
      if (ctx && ctx.cleanup) {
        ctx.cleanup();
      }
    };
  }, []);

  return (
    <section className="section section-point" ref={sectionRef}>
      <div className="container">
        <div className="intro-section">
          <h1 className="main-title">명 컨</h1>
          <h1 className="main-title">함 셉</h1>
        </div>

        <Spacer size={10} />
        <div className="card-container" ref={cardRef}>
          <BusinessCard
            name="박대성"
            team="테크팀"
            phone="010-6533-1985"
            email="pds@alipeople.kr"
            showLogo={true}
            className="ali-card"
          />
        </div>

        <Spacer size={20} />

        <h1 className="point-title">평범해 보이는 명함이죠?</h1>
        <h1 className="point-title">사실은...</h1>

        <h1 className="point-title">
          이 명함은 <span className="emphasis-text">가려지기 위해</span>{" "}
          존재합니다
        </h1>

        <div className="demo-section">
          <div className="cards-container">
            <div className="ali-card-base">
              <BusinessCard
                name="박대성"
                team="테크팀"
                phone="010-6533-1985"
                email="pds@alipeople.kr"
                showLogo={true}
              />
              <div className="logo-overlay"></div>
            </div>
          </div>

          <div className="logo-question">
            <h3 className="question-title">명함의 로고를 왜 가리죠? 🤔</h3>
            <p className="question-subtitle">
              보통 명함은 우리를 드러내기 위한 거죠.
              <br />
              하지만 우리는 달라야 한다고 믿습니다.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Section1Point;
